import React, { cloneElement, useEffect, useMemo, useState } from "react";
import { Checkbox, Radio } from "@mui/material";
import styles from "./FilterContent.module.css";
import { FILTER_TYPE } from "../../filters/common-filters";
import { IconComponent } from "../../../icon-component";
import { FilterWrapper } from "../..";
import { useEffectOnce } from "../../../../hooks";
import { LabeledCheckbox } from "../../../checkboxes";
import { ToggleButtons } from "../../../toggles";
import { isSegFeatureEnabled, translate } from "../../../../providers";
import { isNonEmptyArray, isNonEmptyObject, isObject } from "../../../../utils";
import { CustomTextField } from "../../../text-fields";

const FilterContent = ({
	dynamicItems,
	falseLabel,
	filterKey,
	forceOpen,
	items,
	label,
	multiSelection = false,
	onApply,
	onChange,
	renderer,
	trueLabel,
	type,
	value,
	labelKey,
	valueKey,
	readOnly,
	disablePortal,
	hasDateSelector = false,
	hasIsSeparator = false,
	hasAndOrOperator = false,
	resizable = false,
	disabled = false,
	...props
}) => {
	const [checked, setChecked] = useState(value || []);
	const [search, setSearch] = useState("");
	const handleCheck = (key) => {
		if (readOnly) {
			return;
		}
		if (Array.isArray(checked) && !hasIsSeparator && !hasAndOrOperator) {
			const newChecked = checked.includes(key) ? checked.filter((item) => item !== key) : [...checked, key];
			setChecked(newChecked);
			onChange(isNonEmptyArray(newChecked) ? newChecked : undefined);
		} else if (isObject(checked) && (hasIsSeparator || hasAndOrOperator)) {
			const normalizedChecked = {
				is: checked.is,
				elements: isNonEmptyArray(checked.elements) ? checked.elements : [],
			};
			Object.keys(checked).forEach((k) => {
				if (!["elements", "is"].includes(k) && !Number.isNaN(Number(k))) {
					if (!normalizedChecked.elements.includes(checked[k])) {
						normalizedChecked.elements.push(checked[k]);
					}
				}
			});
			const elements = normalizedChecked.elements || [];
			const newElements = elements.includes(key) ? elements.filter((item) => item !== key) : [...elements, key];
			const newChecked = { ...normalizedChecked, elements: newElements };
			setChecked(newChecked);
			onChange(newElements.length > 0 ? newChecked : undefined);
		}
	};
	const isChecked = (checkedItem, item, key) => {
		const itemValue = key ? item[key] : item;
		if (Array.isArray(checkedItem)) {
			return checkedItem.includes(itemValue);
		}
		if (isObject(checkedItem)) {
			if (Array.isArray(checkedItem.elements)) {
				return checkedItem.elements.includes(itemValue);
			}
		}
		return false;
	};
	useEffectOnce(
		() => {
			setChecked(value || []);
		},
		[value],
		() => value?.length > 0
	);
	useEffect(() => {
		if (value === undefined) {
			setChecked([]);
		} else {
			setChecked(value);
		}
	}, [value]);

	const currentItems = useMemo(() => dynamicItems || items, [dynamicItems, items]);
	const handleSearch = (searchValue) => {
		setSearch(searchValue);
	};
	const handleChange = () => {
		onChange(!value, true);
	};
	const handleChangeTextValue = (e) => {
		onChange(e.target.value);
	};
	const handleChangeRadioGroup = (newValue) => {
		onChange(newValue);
	};
	return (
		<>
			{(type !== FILTER_TYPE.BOOLEAN && (
				<FilterWrapper
					disabled={disabled}
					disablePortal={disablePortal}
					filterKey={filterKey}
					forceOpen={forceOpen}
					hasAndOrOperator={hasAndOrOperator}
					hasDateSelector={hasDateSelector}
					hasIsSeparator={hasIsSeparator}
					label={label}
					onApply={onApply}
					onChange={onChange}
					{...props}
					readOnly={readOnly}
					resizable={resizable}
					search={search}
					value={value}
					onSearch={handleSearch}
				>
					<div className={styles.list__content}>
						{type === FILTER_TYPE.SIMPLE_LIST &&
							multiSelection &&
							((isNonEmptyObject(currentItems) && Object.entries(currentItems)) || []).map(
								([key, item]) =>
									(isSegFeatureEnabled(item.segFlag) || !item.segFlag) && (
										<div
											key={key}
											className={styles.list_itemContainer}
											role="presentation"
											onClick={() => {
												handleCheck(valueKey ? item[valueKey] : item);
											}}
										>
											<div className={styles.list__rowHead}>
												<Checkbox
													checked={isChecked(checked, item, valueKey)}
													classes={{ root: styles.list__checkbox }}
													disabled={readOnly}
													size="small"
												/>
												<div className={styles.list__helperInformation}>
													{item?.iconName && item?.iconPlacement === "left" && (
														<IconComponent color={item.color} icon={item.iconName} />
													)}
													<span
														className={styles.list__rowLabel}
														data-icon-placement-left={item?.iconPlacement === "left"}
													>
														{labelKey ? translate(item[labelKey]) : item}
													</span>
													{item?.iconName &&
														(item?.iconPlacement === "right" || !item?.iconPlacement) && (
															<IconComponent color={item.color} icon={item.iconName} />
														)}
													{item?.description && (
														<div className={styles.list__rowDescription}>
															{translate(item.description)}
														</div>
													)}
												</div>
											</div>
										</div>
									)
							)}
						{type === FILTER_TYPE.SIMPLE_LIST &&
							!multiSelection &&
							(Object.entries(currentItems) || []).map(([key, item]) => (
								<div
									key={key}
									role="presentation"
									onClick={() => {
										handleChangeRadioGroup(valueKey ? item[valueKey] : item);
									}}
								>
									<div className={styles.list__helperInformation}>
										<Radio
											checked={
												valueKey
													? JSON.stringify(value) === JSON.stringify(item[valueKey])
													: value === item
											}
											className={styles.radio}
											color="primary"
										/>
										{item?.iconName && item?.iconPlacement === "left" && (
											<IconComponent color={item.color} icon={item.iconName} />
										)}
										<span
											className={styles.list__rowLabel}
											data-icon-placement-left={item?.iconPlacement === "left"}
										>
											{labelKey ? translate(item[labelKey]) : item}
										</span>
										{item?.iconName &&
											(item?.iconPlacement === "right" || !item?.iconPlacement) && (
												<IconComponent color={item.color} icon={item.iconName} />
											)}
										{item?.description && (
											<div className={styles.list__rowDescription}>
												{translate(item.description)}
											</div>
										)}
									</div>
								</div>
							))}
						{type === FILTER_TYPE.TEXT_FIELD && (
							<CustomTextField
								disabled={readOnly}
								error={value?.length === 0}
								helperText={translate("common:component.filters.input-filter.helper")}
								placeholder={translate(label)}
								size="medium"
								value={value || ""}
								onChange={handleChangeTextValue}
							/>
						)}
						{!!renderer &&
							cloneElement(renderer, {
								filterKey,
								value,
								search,
								onChange,
								...props,
							})}
					</div>
				</FilterWrapper>
			)) ||
				(trueLabel && falseLabel && (
					<ToggleButtons
						exclusive
						content={[
							{
								value: false,
								label: translate(falseLabel),
							},
							{ value: true, label: translate(trueLabel) },
						]}
						value={value}
						onChange={handleChange}
					/>
				)) || (
					<LabeledCheckbox
						disabled={readOnly}
						label={translate(label)}
						labelPlacement="end"
						value={value}
						onChange={handleChange}
					/>
				)}
		</>
	);
};

export default FilterContent;
